import { useCallback, useMemo } from 'react';
import {
  MaxPotentialKpiBlock,
  OrderRulesDashboardResponse,
  OrderRulesMaxPotentialLinkedSite,
  OrderRulesMaxPotentialRow,
} from '../orderRulesApi';

export interface MaxPotentialDisplayRow {
  rowType: 'parent' | 'child' | 'no-layer';
  key: string;
  region: string;
  market: string;
  buildLocation: string;
  skuSlots: string;
  lastUpdatedAt: string;
  layerPerformancePct: string;
  layerPerformanceValue: number;
  maxPlanPct: string;
  optimizedPlanPct: string;
  maxPotentialPct: string;
  maxPotExecPct: string;
  estimatedLaborSavings: string;
  isExpandable?: boolean;
  isExpanded?: boolean;
  siteType?: 'build' | 'cross-dock';
}

interface MetricCard {
  key: string;
  label: string;
  value: string;
  subLabel: string;
  tip: string;
}

interface UseOrderRulesDerivedDataArgs {
  orderRulesData: OrderRulesDashboardResponse | null;
  showSitesWithoutLayerLine: boolean;
  expandedMaxPotentialRows: Record<string, boolean>;
}

const formatInt = (value: number | null | undefined): string => Number(value ?? 0).toLocaleString('en-US');
const formatMoney = (value: number | null | undefined): string => `$${Number(value ?? 0).toLocaleString('en-US')}`;

export const useOrderRulesDerivedData = ({
  orderRulesData,
  showSitesWithoutLayerLine,
  expandedMaxPotentialRows,
}: UseOrderRulesDerivedDataArgs) => {
  const normalizedRecommendationRows = useMemo(() => {
    return (orderRulesData?.recommendations ?? []).map((row) => ({
      ...row,
      potentialCases: formatInt(row.potentialCases),
      deliveryDaysPerWeek:
        Number.isInteger(row.deliveryDaysPerWeek) ? String(row.deliveryDaysPerWeek) : Number(row.deliveryDaysPerWeek ?? 0).toFixed(1),
      casesPerLayer: formatInt(row.casesPerLayer),
      pyPlus6AvgCases: formatInt(row.pyPlus6AvgCases),
      pyMinus6AvgCases: formatInt(row.pyMinus6AvgCases),
      cyMinus6AvgCases: formatInt(row.cyMinus6AvgCases),
    }));
  }, [orderRulesData?.recommendations]);

  const normalizedOpenRecommendationRows = useMemo(() => {
    return (orderRulesData?.openRecommendations ?? []).map((row) => ({
      ...row,
      total: formatInt((row.high ?? 0) + (row.medium ?? 0) + (row.low ?? 0)),
      high: formatInt(row.high),
      medium: formatInt(row.medium),
      low: formatInt(row.low),
    }));
  }, [orderRulesData?.openRecommendations]);

  const normalizedSalesRows = useMemo(() => {
    return (orderRulesData?.salesDetails ?? []).map((row) => ({
      ...row,
      layer: formatInt(row.layer),
      avgCases: formatInt(Math.round(row.avgCases)),
    }));
  }, [orderRulesData?.salesDetails]);

  const normalizedCustomerRows = useMemo(() => {
    return (orderRulesData?.customerUtilization ?? []).map((row) => ({
      ...row,
      enrolledSkus: formatInt(row.enrolledSkus),
      eligibleSkus: formatInt(row.eligibleSkus),
      performanceValue: row.eligibleSkus > 0 ? Math.round((row.enrolledSkus / row.eligibleSkus) * 100) : 0,
      performancePct: row.eligibleSkus > 0 ? `${Math.round((row.enrolledSkus / row.eligibleSkus) * 100)}%` : '0%',
    }));
  }, [orderRulesData?.customerUtilization]);

  const normalizedSkuRows = useMemo(() => {
    return (orderRulesData?.skuUtilization ?? []).map((row) => ({
      ...row,
      enrolledCustomers: formatInt(row.enrolledCustomers),
      totalLargeFormat: formatInt(row.totalLargeFormat),
      performanceValue: row.totalLargeFormat > 0 ? Math.round((row.enrolledCustomers / row.totalLargeFormat) * 100) : 0,
      performancePct: row.totalLargeFormat > 0 ? `${Math.round((row.enrolledCustomers / row.totalLargeFormat) * 100)}%` : '0%',
    }));
  }, [orderRulesData?.skuUtilization]);

  const calculateEstimatedLaborSavings = useCallback((fromPct: number, toPct: number, volume: number) => {
    const nationalLayerCasesPerHour = 95;
    const loadedLaborRate = 30;
    return Math.round(((toPct - fromPct) / 100) * (volume / nationalLayerCasesPerHour) * loadedLaborRate);
  }, []);

  /**
   * Build metric cards from the API's pre-computed kpiBlocks when present,
   * otherwise fall back to volume-weighted averages across the table rows.
   */
  const maxPotentialMetricCards = useMemo<MetricCard[]>(() => {
    const emptyCards: MetricCard[] = [
      {
        key: 'lp',
        label: 'Layer Performance',
        value: '--',
        subLabel: 'Weighted avg, current',
        tip: 'Volume-weighted average actual layer performance across the build locations in the current selection.',
      },
      {
        key: 'max-plan',
        label: 'Max Plan',
        value: '--',
        subLabel: 'SKUs already in Voice Pick',
        tip: 'Volume-weighted average Max Plan across the build locations in the current selection.',
      },
      {
        key: 'opt',
        label: 'Optimized Plan',
        value: '--',
        subLabel: 'All eligible SKUs configured',
        tip: 'Volume-weighted average Optimized Plan across the build locations in the current selection.',
      },
      {
        key: 'max-pot',
        label: 'Max Potential',
        value: '--',
        subLabel: 'High and Medium recs applied',
        tip: 'Volume-weighted average Max Potential across the build locations in the current selection.',
      },
      {
        key: 'mp-exec',
        label: 'Max Potential Execution',
        value: '--',
        subLabel: 'Layer performance vs max potential',
        tip: 'Layer Performance divided by Max Potential, rounded to the nearest integer. How much of the achievable layer opportunity is being executed today.',
      },
    ];

    const kpiBlocks: MaxPotentialKpiBlock | undefined = orderRulesData?.maxPotentialKpiBlocks;
    const rows = orderRulesData?.maxPotential ?? [];

    if (!kpiBlocks && rows.length === 0) return emptyCards;

    // Use the server-computed KPI block when available (preferred path)
    if (kpiBlocks) {
      const fmt = (v: number | undefined) => (v != null ? `${Number(v.toFixed(2))}%` : '--');
      const execPct = kpiBlocks.maxPotExecPct ?? kpiBlocks.maxPotentialExecutionPct;
      return [
        { key: 'lp', label: 'Layer Performance', value: fmt(kpiBlocks.layerPerformancePct), subLabel: 'Weighted avg, current', tip: emptyCards[0].tip },
        { key: 'max-plan', label: 'Max Plan', value: fmt(kpiBlocks.maxPlanPct), subLabel: 'SKUs already in Voice Pick', tip: emptyCards[1].tip },
        { key: 'opt', label: 'Optimized Plan', value: fmt(kpiBlocks.optimizedPlanPct), subLabel: 'All eligible SKUs configured', tip: emptyCards[2].tip },
        { key: 'max-pot', label: 'Max Potential', value: fmt(kpiBlocks.maxPotentialPct), subLabel: 'High and Medium recs applied', tip: emptyCards[3].tip },
        { key: 'mp-exec', label: 'Max Potential Execution', value: execPct != null ? fmt(execPct) : '--', subLabel: 'Layer performance vs max potential', tip: emptyCards[4].tip },
      ];
    }

    // Fallback: compute from table rows (no volume data in new API — derive execution from layer/maxPot)
    const totalRows = rows.length;
    const avg = (key: keyof Pick<OrderRulesMaxPotentialRow, 'layerPerformancePct' | 'maxPlanPct' | 'optimizedPlanPct' | 'maxPotentialPct'>) =>
      totalRows > 0 ? Math.round(rows.reduce((sum, row) => sum + row[key], 0) / totalRows) : 0;
    const layerPct = avg('layerPerformancePct');
    const maxPotPct = avg('maxPotentialPct');
    const execPct = maxPotPct > 0 ? Math.round((layerPct / maxPotPct) * 100) : 0;
    return [
      { key: 'lp', label: 'Layer Performance', value: `${layerPct}%`, subLabel: 'Avg, current', tip: emptyCards[0].tip },
      { key: 'max-plan', label: 'Max Plan', value: `${avg('maxPlanPct')}%`, subLabel: 'SKUs already in Voice Pick', tip: emptyCards[1].tip },
      { key: 'opt', label: 'Optimized Plan', value: `${avg('optimizedPlanPct')}%`, subLabel: 'All eligible SKUs configured', tip: emptyCards[2].tip },
      { key: 'max-pot', label: 'Max Potential', value: `${maxPotPct}%`, subLabel: 'High and Medium recs applied', tip: emptyCards[3].tip },
      { key: 'mp-exec', label: 'Max Potential Execution', value: `${execPct}%`, subLabel: 'Layer performance vs max potential', tip: emptyCards[4].tip },
    ];
  }, [orderRulesData?.maxPotentialKpiBlocks, orderRulesData?.maxPotential]);

  const maxPotentialExecutionRows = useMemo<MaxPotentialDisplayRow[]>(() => {
    if (showSitesWithoutLayerLine) {
      return (orderRulesData?.maxPotentialNoLayer ?? []).map((row) => {
        const estimatedLaborSavings = calculateEstimatedLaborSavings(0, row.maxPotentialPct, row.volume);
        return {
          rowType: 'no-layer',
          key: `no-layer-${row.location}`,
          region: row.region,
          market: row.market,
          buildLocation: row.location,
          skuSlots: '10',
          lastUpdatedAt: '',
          layerPerformancePct: '0%',
          layerPerformanceValue: 0,
          maxPlanPct: '-',
          optimizedPlanPct: '-',
          maxPotentialPct: `${row.maxPotentialPct}%`,
          maxPotExecPct: '-',
          estimatedLaborSavings: formatMoney(estimatedLaborSavings),
        };
      });
    }

    const rows: MaxPotentialDisplayRow[] = [];

    (orderRulesData?.maxPotential ?? []).forEach((buildRow) => {
      const parentKey = `build-${buildRow.buildLocation}`;
      const linkedSites: OrderRulesMaxPotentialLinkedSite[] =
        buildRow.linkedSites && buildRow.linkedSites.length > 0
          ? buildRow.linkedSites
          : [
              {
                location: buildRow.buildLocation,
                skuSlots: buildRow.skuSlots,
                layerPerformancePct: buildRow.layerPerformancePct,
                maxPlanPct: buildRow.maxPlanPct,
                optimizedPlanPct: buildRow.optimizedPlanPct,
                maxPotentialPct: buildRow.maxPotentialPct,
                volume: buildRow.volume,
                siteType: 'build',
              },
            ];

      const totalVolume = linkedSites.reduce((sum, site) => sum + site.volume, 0);

      // When the API doesn't return volume (all zeros), use the row's own values directly
      // for a single-site parent, or a simple average for multi-site rollups.
      // When volume data is present, use proper volume-weighted average.
      const rollupPct = (key: 'layerPerformancePct' | 'maxPlanPct' | 'optimizedPlanPct' | 'maxPotentialPct'): number => {
        if (linkedSites.length === 1) {
          // Single site — no aggregation needed, use the value directly
          return linkedSites[0][key];
        }
        if (totalVolume > 0) {
          // Multi-site with volume: volume-weighted average
          return linkedSites.reduce((sum, site) => sum + site[key] * site.volume, 0) / totalVolume;
        }
        // Multi-site without volume: simple average
        return linkedSites.reduce((sum, site) => sum + site[key], 0) / linkedSites.length;
      };

      const rollupLayer = rollupPct('layerPerformancePct');
      const rollupMaxPlan = rollupPct('maxPlanPct');
      const rollupOptimized = rollupPct('optimizedPlanPct');
      const rollupMaxPotential = rollupPct('maxPotentialPct');
      const rollupGap = rollupMaxPotential - rollupLayer;
      const rollupSlots = linkedSites.reduce((sum, site) => sum + site.skuSlots, 0);
      const rollupSavings = linkedSites.reduce(
        (sum, site) => sum + calculateEstimatedLaborSavings(site.layerPerformancePct, site.maxPotentialPct, site.volume),
        0,
      );

      const isExpandable = buildRow.linkedSites != null && buildRow.linkedSites.length > 0;
      const isExpanded = !!expandedMaxPotentialRows[parentKey];

      rows.push({
        rowType: 'parent',
        key: parentKey,
        region: buildRow.region,
        market: buildRow.market,
        buildLocation: buildRow.buildLocation,
        skuSlots: formatInt(rollupSlots),
        // Use the real API date field; fall back gracefully
        lastUpdatedAt: buildRow.lastUpdateOrderRules ?? '',
        layerPerformancePct: `${Number(rollupLayer.toFixed(2))}%`,
        layerPerformanceValue: rollupLayer,
        maxPlanPct: `${Number(rollupMaxPlan.toFixed(2))}%`,
        optimizedPlanPct: `${Number(rollupOptimized.toFixed(2))}%`,
        maxPotentialPct: `${Number(rollupMaxPotential.toFixed(2))}%`,
        // Use pre-computed maxPotExecPct from API when available; fall back to derived value
        maxPotExecPct: buildRow.maxPotExecPct != null
          ? `${Number(buildRow.maxPotExecPct.toFixed(2))}%`
          : rollupLayer > 0
          ? `${Number(((rollupLayer / rollupMaxPotential) * 100).toFixed(2))}%`
          : '-',
        // Use pre-computed estCostSavings ($/wk) from API; fall back to formula
        estimatedLaborSavings: buildRow.estCostSavings != null
          ? formatMoney(buildRow.estCostSavings)
          : formatMoney(rollupSavings),
        isExpandable,
        isExpanded,
      });

      if (isExpandable && isExpanded) {
        linkedSites.forEach((site, siteIndex) => {
          const siteGap = site.maxPotentialPct - site.layerPerformancePct;
          rows.push({
            rowType: 'child',
            key: `${parentKey}-site-${siteIndex}`,
            region: '',
            market: '',
            buildLocation: site.location,
            skuSlots: formatInt(site.skuSlots),
            lastUpdatedAt: '',
            layerPerformancePct: `${Number(site.layerPerformancePct.toFixed(2))}%`,
            layerPerformanceValue: site.layerPerformancePct,
            maxPlanPct: `${Number(site.maxPlanPct.toFixed(2))}%`,
            optimizedPlanPct: `${Number(site.optimizedPlanPct.toFixed(2))}%`,
            maxPotentialPct: `${Number(site.maxPotentialPct.toFixed(2))}%`,
            maxPotExecPct: site.layerPerformancePct > 0 && site.maxPotentialPct > 0
              ? `${Number(((site.layerPerformancePct / site.maxPotentialPct) * 100).toFixed(2))}%`
              : '-',
            estimatedLaborSavings: formatMoney(
              calculateEstimatedLaborSavings(site.layerPerformancePct, site.maxPotentialPct, site.volume),
            ),
            siteType: site.siteType,
          });
        });
      }
    });

    return rows;
  }, [
    calculateEstimatedLaborSavings,
    expandedMaxPotentialRows,
    orderRulesData?.maxPotential,
    orderRulesData?.maxPotentialNoLayer,
    showSitesWithoutLayerLine,
  ]);

  const normalizedCheckCrossdockRows = useMemo(() => {
    return (orderRulesData?.orderRulesCheckCrossdock ?? []).map((row) => ({
      ...row,
      orderRule: row.orderRule == null ? 'N/A' : formatInt(row.orderRule),
      flag: row.reviewFlag ? 'Review' : 'OK',
    }));
  }, [orderRulesData?.orderRulesCheckCrossdock]);

  // Build Location rows with their serving cross dock rows (same material) nested
  // underneath, matching the consolidated Order Rule Check table — the standalone
  // Crossdocks table is retired in favor of an expandable child-row pattern.
  const normalizedCheckBuildRows = useMemo(() => {
    return (orderRulesData?.orderRulesCheckBuild ?? []).map((row) => {
      const crossdocks = normalizedCheckCrossdockRows.filter(
        (xdRow) => xdRow.buildLocation === row.location && xdRow.materialNumber === row.materialNumber,
      );
      return {
        ...row,
        buildPallet: formatInt(row.buildPallet),
        packSpec: formatInt(row.packSpec),
        orderRule: row.orderRule == null ? 'N/A' : formatInt(row.orderRule),
        flag: row.reviewFlag ? 'Review' : 'OK',
        crossdocks,
        isExpandable: crossdocks.length > 0,
      };
    });
  }, [orderRulesData?.orderRulesCheckBuild, normalizedCheckCrossdockRows]);

  return {
    normalizedRecommendationRows,
    normalizedOpenRecommendationRows,
    normalizedSalesRows,
    normalizedCustomerRows,
    normalizedSkuRows,
    maxPotentialMetricCards,
    maxPotentialExecutionRows,
    normalizedCheckBuildRows,
    normalizedCheckCrossdockRows,
  };
};
